/**
 * 04 · COGS por Solución × tipo de costo y 05 · Investment por business case:
 * la misma lista rankeada + panel de detalle, instanciada dos veces (`kind`).
 * Con 20-30 líneas el tornado deja de servir: la lista jerarquiza, el panel
 * profundiza. Mes y acumulado comparan contra el plan de esos mismos meses
 * (dólares = desvío, % = ejecución); semestre y año contra un plan que sigue
 * corriendo (dólares = saldo, % = consumido). El orden es siempre por dólares,
 * nunca por porcentaje. El período "Mes" tiene su propio selector (cualquier
 * mes cerrado) y el toggle Payroll / Non-payroll / Total suma el payroll como
 * una sola fila "S&B (agregado)", nunca por rol.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { computeBreakdown, groupCode, type PnlBreakdownPeriod } from '../../../../../shared/finance-pnl';
import type { PnlCostLine, PnlDashboardData } from '../../../../../shared/types/finance-pnl';
import { C, deltaColor, usePayOptions, type PayMode, type PnlFmt } from './pnl-format';
import { PnlMonthSelect, PnlNote, SectionHead, Seg, SegLabel, type NoteTag } from './pnl-ui';

export type BreakdownKind = 'investment' | 'cogs';

/** Qué cambia entre las dos instancias: la línea, el namespace de i18n propio y las notas. */
const KIND: Record<BreakdownKind, { line: PnlCostLine; ns: string; notes: Array<[NoteTag, string]> }> = {
  investment: {
    line: 'investment',
    ns: 'budget_control.investment',
    notes: [
      ['idea', 'periods'],
      ['new', 'month'],
      ['idea', 'order'],
      ['color', 'color'],
      ['warn', 'payroll'],
      ['decision', 'decision'],
    ],
  },
  cogs: {
    line: 'cogs',
    ns: 'budget_control.cogs',
    notes: [
      ['idea', 'idea'],
      ['new', 'month'],
      ['warn', 'payroll'],
      ['rule', 'rule'],
      ['source', 'source'],
    ],
  },
};

export function PnlBreakdown({ data, month, fmt, notes, kind, num }: { data: PnlDashboardData; month: number; fmt: PnlFmt; notes: boolean; kind: BreakdownKind; num: string }) {
  const { t } = useTranslation();
  const cfg = KIND[kind];
  const pay$ = usePayOptions();
  const [group, setGroup] = useState<string>('all');
  const [period, setPeriod] = useState<PnlBreakdownPeriod>('ytd');
  const [pay, setPay] = useState<PayMode>('nonPayroll');
  const [localMonth, setLocalMonth] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // El mes de la sección sigue al de la cabecera; "Mes" permite mirar otro cerrado sin mover el resto de la página.
  useEffect(() => setLocalMonth(null), [month]);
  const effMonth = period === 'month' && localMonth !== null && localMonth >= 1 && localMonth <= data.closedMonth ? localMonth : month;
  const closedMonths = useMemo(() => Array.from({ length: data.closedMonth }, (_, i) => i + 1), [data.closedMonth]);

  const view = useMemo(
    () => computeBreakdown(data, { line: cfg.line, month: effMonth, period, pay, group: group === 'all' ? undefined : group }),
    [data, cfg.line, effMonth, period, pay, group]
  );
  const selected = view.cases.find((c) => c.id === selectedId) ?? view.cases[0] ?? null;
  const isVar = view.mode === 'var';
  const semester = effMonth <= 6 ? 'H1' : 'H2';
  const from = (ms: number[]) => fmt.monthShort(ms[0]);
  const to = (ms: number[]) => fmt.monthShort(ms[ms.length - 1]);
  const catLabel = (c: string) => (c === 'S&B' ? pay$.sbLabel : c);
  const groupLabel = (g: string) => g;
  const i = (key: string, vars?: Record<string, unknown>) => t(`budget_control.investment.${key}`, vars);

  const periodText: Record<PnlBreakdownPeriod, string> = {
    month: i('sub_month', { month: fmt.monthLong(effMonth), year: data.year }),
    ytd: i('sub_ytd', { from: fmt.monthShort(1), to: fmt.monthShort(effMonth), year: data.year }),
    semester: i('sub_semester', { sem: semester, from: from(view.actualMonths), to: to(view.actualMonths), pfrom: from(view.planMonths), pto: to(view.planMonths) }),
    year: i('sub_year', { year: data.year, from: from(view.actualMonths), to: to(view.actualMonths) }),
  };
  const planLabel: Record<PnlBreakdownPeriod, string> = {
    month: i('plan_month', { month: fmt.monthLong(effMonth) }),
    ytd: i('plan_ytd'),
    semester: i('plan_range', { from: from(view.planMonths), to: to(view.planMonths) }),
    year: i('plan_range', { from: from(view.planMonths), to: to(view.planMonths) }),
  };
  const execTitle: Record<PnlBreakdownPeriod, string> = {
    month: i('stat_exec_month', { month: fmt.monthLong(effMonth) }),
    ytd: i('stat_exec_range', { from: fmt.monthShort(1), to: fmt.monthShort(effMonth) }),
    semester: i('stat_exec_semester', { sem: semester }),
    year: i('stat_exec_range', { from: from(view.actualMonths), to: to(view.actualMonths) }),
  };
  const runWhat = period === 'semester' ? i('of_semester') : i('of_year');
  const pctTitle = isVar ? (period === 'month' ? i('stat_pct_month') : i('stat_pct_ytd')) : i('stat_pct_run', { what: runWhat });
  const amtTitle = isVar ? (period === 'month' ? i('stat_amt_month') : i('stat_amt_ytd')) : i('stat_amt_run', { what: runWhat });

  const amountText = (a: number) => (isVar ? fmt.sgnK(a) : fmt.k(a));
  const amountColor = (a: number) => {
    if (isVar) return deltaColor(a <= 0, a);
    if (a < -50) return C.unfav;
    return Math.abs(a) < 50 ? C.ink3 : C.ink;
  };
  const barColor = (a: number) => (isVar ? deltaColor(a <= 0, a) : a < -50 ? C.unfav : C.neutral);
  const pctText = (pct: number | null, actual: number) => (pct !== null ? `${Math.round(pct)}%` : Math.abs(actual) > 49 ? i('no_plan') : '—');
  const pctColor = (pct: number | null, actual: number) => (pct === null ? (Math.abs(actual) > 49 ? C.warn : C.ink3) : undefined);

  const mx = Math.max(...view.cases.map((c) => Math.abs(c.amount)), 1);
  const active = view.cases.filter((c) => c.actual > 49 || c.plan > 49).length;
  const totalColor = isVar ? deltaColor(view.amount <= 0, view.amount) : C.ink;
  const colAmount = isVar ? i('col_var') : i('col_bal');
  const colPct = isVar ? i('col_exec') : i('col_cons');
  const payText = pay$.longLabel(pay);

  return (
    <section>
      <SectionHead num={num} title={t(`${cfg.ns}.title`, { n: view.cases.length })} sub={t(`${cfg.ns}.subtitle`)} />
      <Card className="p-4 shadow-xs">
        <div className="mb-3.5 grid grid-cols-1 gap-3 border-b border-border pb-3.5 md:grid-cols-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">{execTitle[period]}</span>
            <span className="font-mono text-[22px] font-semibold leading-tight tracking-tight tabular-nums">{fmt.k(view.actual)}</span>
            <span className="text-[11.5px] text-muted-foreground">{i('stat_vs', { plan: fmt.k(view.plan), planLabel: planLabel[period] })}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">{pctTitle}</span>
            <span className="font-mono text-[22px] font-semibold leading-tight tracking-tight tabular-nums">{view.pct === null ? '–' : `${Math.round(view.pct)}%`}</span>
            <span className="text-[11.5px] text-muted-foreground">{periodText[period]}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">{amtTitle}</span>
            <span className="font-mono text-[22px] font-semibold leading-tight tracking-tight tabular-nums" style={{ color: totalColor }}>{amountText(view.amount)}</span>
            <span className="text-[11.5px] text-muted-foreground">
              {isVar ? i('stat_amt_sub_var', { pct: view.plan ? fmt.sgnPct((view.actual / view.plan - 1) * 100, 0) : '–', pay: payText }) : i('stat_amt_sub_run', { pay: payText })}
            </span>
          </div>
        </div>

        <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
          <Seg<string>
            ariaLabel={t(`${cfg.ns}.cohort_aria`)}
            value={group}
            onChange={setGroup}
            options={[{ value: 'all', label: t(`${cfg.ns}.cohort_all`) }, ...view.groups.map((g) => ({ value: g, label: groupLabel(g) }))]}
          />
          <SegLabel>{t('budget_control.pnl.period')}</SegLabel>
          <Seg<PnlBreakdownPeriod>
            ariaLabel={t('budget_control.pnl.period')}
            value={period}
            onChange={setPeriod}
            options={[
              { value: 'month', label: t('budget_control.pnl.month') },
              { value: 'ytd', label: `${fmt.monthShort(1)}–${fmt.monthShort(effMonth)}` },
              { value: 'semester', label: `${semester} · ${fmt.monthShort(effMonth <= 6 ? 1 : 7)}–${fmt.monthShort(effMonth <= 6 ? 6 : 12)}` },
              { value: 'year', label: i('per_year', { year: data.year }) },
            ]}
          />
          {period === 'month' && (
            <PnlMonthSelect value={effMonth} onChange={setLocalMonth} months={closedMonths} year={data.year} fmt={fmt} ariaLabel={t('budget_control.pnl.month')} className="w-[130px]" />
          )}
          <SegLabel>{t('budget_control.pay.label')}</SegLabel>
          <Seg<PayMode> ariaLabel={t('budget_control.pay.label')} value={pay} onChange={setPay} options={pay$.options} />
        </div>

        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[1.35fr_1fr]">
          <div>
            <div className="grid grid-cols-[1fr_96px_66px_62px] gap-2 border-b border-border px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              <span>{t(`${cfg.ns}.col_case`)}</span>
              <span />
              <span className="text-right">{colAmount}</span>
              <span className="text-right">{colPct}</span>
            </div>
            <div className="max-h-[498px] overflow-y-auto pr-1.5">
              {view.cases.map((c) => {
                const isSel = selected?.id === c.id;
                const w = isVar ? Math.max((Math.abs(c.amount) / mx) * 49, 0.6) : Math.max((Math.abs(c.amount) / mx) * 100, 0.6);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    aria-pressed={isSel}
                    className={cn(
                      'grid w-full grid-cols-[1fr_96px_66px_62px] items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left hover:bg-muted',
                      isSel && 'border-primary/30 bg-accent'
                    )}
                  >
                    <span className="truncate text-xs" title={`${c.name} · ${groupLabel(c.group)}`}>
                      <span className="mr-1.5 rounded-sm border border-border px-[3px] align-[1px] text-[9.5px] font-bold tracking-wider text-muted-foreground">{groupCode(c.group)}</span>
                      {c.short}
                    </span>
                    <span className={cn('relative h-[15px]', !isVar && 'rounded-[3px] bg-muted')}>
                      {isVar && <span className="absolute bottom-[-2px] top-[-2px] left-1/2 w-px" style={{ background: C.hairStrong }} />}
                      <span
                        className="absolute top-[2px] h-[11px] rounded-[2px]"
                        style={{ background: barColor(c.amount), width: `${w}%`, ...(isVar ? (c.amount >= 0 ? { left: '50%' } : { right: '50%' }) : { left: 0 }) }}
                      />
                    </span>
                    <span className="text-right font-mono text-xs font-semibold tabular-nums" style={{ color: amountColor(c.amount) }}>{amountText(c.amount)}</span>
                    <span className="text-right font-mono text-[11.5px] tabular-nums text-muted-foreground" style={{ color: pctColor(c.pct, c.actual) }}>{pctText(c.pct, c.actual)}</span>
                  </button>
                );
              })}
              {view.cases.length === 0 && (
                <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">{i('empty')}</div>
              )}
            </div>
            <div className="mt-1 border-t border-border px-2 pt-1.5 text-[11px] text-muted-foreground">
              {t(`${cfg.ns}.count_line`, {
                n: view.cases.length,
                cohort: group === 'all' ? '' : t(`${cfg.ns}.in_cohort`, { cohort: groupLabel(group) }),
                active,
              })}
            </div>
          </div>

          {selected && (
            <div className="rounded-lg border border-border bg-muted/60 p-4">
              <div className="text-[14.5px] font-semibold tracking-tight">{selected.name}</div>
              <div className="mb-3 text-[11.5px] text-muted-foreground">{i('detail_sub', { cohort: groupLabel(selected.group), pay: payText, period: periodText[period] })}</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  [i('real'), fmt.k(selected.actual), undefined],
                  [i('plan'), fmt.k(selected.plan), undefined],
                  [isVar ? i('col_var') : i('available'), amountText(selected.amount), amountColor(selected.amount)],
                  [isVar ? i('execution') : i('consumed'), selected.pct !== null ? `${Math.round(selected.pct)}%` : Math.abs(selected.actual) > 49 ? i('no_plan') : i('no_move'), pctColor(selected.pct, selected.actual)],
                ].map(([k, v, color], idx) => (
                  <div key={idx} className="flex flex-col">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{k}</span>
                    <span className="font-mono text-[15px] font-semibold tabular-nums" style={{ color: color as string | undefined }}>{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 border-t border-border pt-3">
                {[
                  [i('real'), selected.actual, 'hsl(var(--primary))'],
                  [i('plan'), selected.plan, C.hairStrong],
                ].map(([label, v, color], idx) => {
                  const mxb = Math.max(selected.actual, selected.plan, 1);
                  return (
                    <div key={idx} className="mb-1.5 grid grid-cols-[38px_1fr_54px] items-center gap-2">
                      <span className="text-[11px] text-muted-foreground">{label as string}</span>
                      <span className="relative h-3 rounded-[3px] bg-card">
                        <span className="absolute left-0 top-0 h-3 rounded-[3px]" style={{ background: color as string, width: `${Math.max(((v as number) / mxb) * 100, 0)}%` }} />
                      </span>
                      <span className="text-right font-mono text-[11.5px] tabular-nums">{fmt.k(v as number)}</span>
                    </div>
                  );
                })}
              </div>
              {selected.categories.length > 0 && (
                <>
                  <div className="mb-1.5 mt-3.5 text-[11px] uppercase tracking-[0.06em] text-muted-foreground">{i('by_category', { what: colAmount })}</div>
                  <div className="grid grid-cols-[1fr_42px_42px_58px_38px] items-center gap-1.5 border-b border-border pb-1 text-[10.5px] text-muted-foreground">
                    <span>{i('col_category')}</span>
                    <span className="text-right">{i('real')}</span>
                    <span className="text-right">{i('plan')}</span>
                    <span className="text-right">{colAmount}</span>
                    <span className="text-right">{colPct}</span>
                  </div>
                  {selected.categories.map((c) => {
                    const cmx = Math.max(...selected.categories.map((x) => Math.abs(x.amount)), 1);
                    const w = (Math.abs(c.amount) / cmx) * (isVar ? 10 : 20);
                    return (
                      <div key={c.category} className="grid grid-cols-[1fr_42px_42px_58px_38px] items-center gap-1.5 border-b border-border py-1.5">
                        <span className="truncate text-[11.5px]" title={catLabel(c.category)}>{catLabel(c.category)}</span>
                        <span className="text-right font-mono text-[11.5px] tabular-nums">{fmt.num(c.actual / 1000)}</span>
                        <span className="text-right font-mono text-[11.5px] tabular-nums">{fmt.num(c.plan / 1000)}</span>
                        <span className="flex items-center justify-end gap-1.5">
                          <span className="relative h-2 w-5 flex-none">
                            <span
                              className="absolute top-0 h-2 rounded-[2px]"
                              style={{ background: barColor(c.amount), width: `${Math.max(w, 0.8)}px`, ...(isVar ? (c.amount >= 0 ? { left: 10 } : { right: 10 }) : { left: 0 }) }}
                            />
                          </span>
                          <span className="font-mono text-[11.5px] tabular-nums" style={{ color: amountColor(c.amount) }}>{amountText(c.amount)}</span>
                        </span>
                        <span className="text-right font-mono text-[11px] tabular-nums text-muted-foreground">{c.pct === null ? '–' : `${Math.round(c.pct)}%`}</span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </Card>
      <PnlNote show={notes} items={cfg.notes.map(([tag, key]) => ({ tag, title: t(`budget_control.notes.${kind}.${key}_title`), text: t(`budget_control.notes.${kind}.${key}_text`) }))} />
    </section>
  );
}
